import type {
  ExamWorksheetPart,
  ParentPairingResponse,
  ParentLinksResponse,
  RedeemCodeResponse,
  PairingCodeInfoResponse,
  AssignmentsResponse,
  AssignmentSummary,
  AssignmentResultResponse,
  ChildSummaryHistoryResponse,
  ChildActivityResponse,
  ParentNotificationsResponse,
  ParentNotificationKind,
} from '@tupai/types';
import { requireAppUpgrade } from '@tupai/shell';
import type { AccessCheckResponse, AppOffering, AppStore } from '@tupai/billing';
import { capture, firstTouchBody } from '@tupai/analytics/client';
import { useAuthStore } from '@/store/auth-store';
import type { AppLocale } from '@/lib/language';
import { templatizePath } from '@/lib/analytics';
import { consentPayload } from '@/lib/consent';
import {
  isSampleId,
  isSampleLink,
  sampleActivity,
  sampleAssignments,
  sampleAssignmentResult,
  sampleReport,
  sampleReportHistory,
} from '@/lib/sample-child';

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.0.0';
const BASE = (import.meta.env.VITE_PARENT_API_URL as string) || 'http://localhost:3006';

export class ApiError extends Error {
  status: number;
  // UI branches on this machine-readable key, never on the message.
  code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function getToken(): string | null {
  return useAuthStore.getState().token;
}

// Instrumented once here, never at call sites; analytics must not change control flow, return value or timing.
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const hasBody = !!options.body;
  const headers: Record<string, string> = {
    // Fastify's JSON parser 400s on Content-Type: application/json with an empty body.
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    'X-App-Version': APP_VERSION,
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const method = (options.method ?? 'GET').toUpperCase();
  const pathTemplate = templatizePath(path);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...options, headers });
  } catch (err) {
    capture('api_request_failed', {
      path: pathTemplate,
      method,
      status: 0,
      error_code: 'network_error',
    });
    throw err;
  }

  if (!res.ok) {
    if (res.status === 426) {
      const detail = await res.json().catch(() => ({}));
      capture('app_upgrade_required', {
        path: pathTemplate,
        status: 426,
        ...(typeof detail.minAppVersion === 'string'
          ? { min_app_version: detail.minAppVersion }
          : {}),
      });
      requireAppUpgrade(detail);
      throw new ApiError(detail.message ?? 'Update required', 426, 'upgrade_required');
    }
    // A late response from an old session must not clear a newer login.
    if (res.status === 401 && token && useAuthStore.getState().token === token) {
      useAuthStore.getState().expireSession();
    }
    const err = await res.json().catch(() => ({}));
    const errorCode = typeof (err as any).error === 'string' ? (err as any).error : undefined;
    capture('api_request_failed', {
      path: pathTemplate,
      method,
      status: res.status,
      ...(errorCode ? { error_code: errorCode } : {}),
    });
    // Only billing feature gates; syllabus 'upgrade_required' and seat-capacity 403s are handled by callers via ApiError.code.
    if (
      res.status === 403 &&
      (errorCode === 'quota_exhausted' || errorCode === 'feature_blocked')
    ) {
      capture('feature_gate_hit', { path: pathTemplate, status: 403, gate: errorCode });
    }
    const message = (err as any).message ?? (err as any).error ?? `API error ${res.status}`;
    throw new ApiError(message, res.status, (err as any).error ?? null);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export function getAppMeta(): Promise<{ minAppVersion: string }> {
  return request('/meta');
}

export function firebaseLogin(
  idToken: string,
  purpose?: 'signup' | 'signin'
): Promise<VerifyOtpResponse> {
  return request('/api/auth/firebase-login', {
    method: 'POST',
    body: JSON.stringify({
      firebase_id_token: idToken,
      ...(purpose ? { purpose } : {}),
      ...firstTouchBody(),
    }),
  });
}

/** Whether a parent account already uses this phone (E.164); asked before the SMS goes out. */
export async function isPhoneRegistered(phone: string): Promise<boolean> {
  const res = await request<{ registered: boolean }>('/api/auth/phone-status', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
  return res.registered;
}

export async function sendOtp(
  phone: string,
  parentName: string,
  purpose: 'signup' | 'signin'
): Promise<void> {
  await request('/api/auth/send-otp', {
    method: 'POST',
    body: JSON.stringify({
      phone,
      parent_name: parentName,
      purpose,
    }),
  });
}

export interface VerifyOtpResponse {
  token: string;
  parent_id: string;
  parent_name: string;
  phone: string;
  // Null for a parent created by this call; sign-in is the app's only read path for it (lib/language.ts).
  preferred_language: string | null;
  // Optional for older servers; setSignupDate() no-ops on undefined.
  created_at?: string;
  /** True when this sign-in created the account (firebase-login). */
  is_new?: boolean;
}

function readBrowserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export async function verifyOtp(
  phone: string,
  otp: string,
  parentName: string
): Promise<VerifyOtpResponse> {
  return request<VerifyOtpResponse>('/api/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({
      phone,
      otp,
      parent_name: parentName,
      timezone: readBrowserTimezone(),
      ...firstTouchBody(),
    }),
  });
}

export interface HouseholdMember {
  role: 'student' | 'parent';
  student_id: string | null;
  parent_id: string | null;
  name: string;
  is_admin: boolean;
  is_billing_owner: boolean;
  syllabus_id: string | null;
}

export interface HouseholdResponse {
  household: {
    // Optional: older servers don't send it.
    id?: string;
    status: string;
    billing_owner: { role: 'student' | 'parent'; name: string } | null;
    members: HouseholdMember[];
    seat_tier: string;
    seats_over_cap: boolean;
    syllabus_over_tier: boolean;
  } | null;
}

export async function getHousehold(): Promise<HouseholdResponse> {
  return request('/api/household');
}

export async function grantHouseholdAdmin(parentId: string): Promise<{ ok: boolean }> {
  return request(`/api/household/admins/${parentId}/grant`, { method: 'POST' });
}

export interface AccessResponse {
  // Only the status union is shared: the parent endpoint doesn't return the rest of AccessCheckResponse.
  status: AccessCheckResponse['status'];
  seat_tier: string | null;
  pricing_tier: string;
  // Display only. pending_effective_at can be null with pending_seat_tier set: Play gives no date for deferred replacements.
  pending_seat_tier?: string | null;
  pending_pricing_tier?: string | null;
  pending_effective_at?: string | null;
  // 'cancelled' means auto-renew is off, not that access stopped; access runs until this instant.
  period_end: string | null;
  // UI signal; web checkout re-checks it server-side before granting a trial.
  trial_eligible: boolean;
  // Store of the household's latest billing event; null without a household or events.
  store?: AppStore | null;
}

// Web only: the Stripe catalog in the same shape native offerings map into.
export async function getWebOfferings(): Promise<Record<string, AppOffering>> {
  const { offerings } = await request<{
    offerings: Record<
      string,
      Omit<AppOffering, 'availablePackages'> & {
        availablePackages: Omit<AppOffering['availablePackages'][number], '_sdk'>[];
      }
    >;
  }>('/api/billing/offerings');
  return Object.fromEntries(
    Object.entries(offerings).map(([id, o]) => [
      id,
      { ...o, availablePackages: o.availablePackages.map((p) => ({ ...p, _sdk: null })) },
    ])
  );
}

// Web only; the server prices the plan and decides the trial.
export async function createCheckoutSession(target: {
  seatTier: string;
  pricingTier: string;
  billingPeriod: 'monthly' | 'yearly';
}): Promise<{ session_id: string; client_secret: string }> {
  return request('/api/billing/checkout-session', {
    method: 'POST',
    body: JSON.stringify({
      seat_tier: target.seatTier,
      pricing_tier: target.pricingTier,
      billing_period: target.billingPeriod,
    }),
  });
}

export async function getCheckoutSessionStatus(
  sessionId: string
): Promise<{ status: 'open' | 'complete' | 'expired' | null; payment_status: string }> {
  return request(`/api/billing/checkout-session/${encodeURIComponent(sessionId)}`);
}

// Web only: card-only checkout when change-plan answers payment_method_required.
export async function createCardSetup(): Promise<{ session_id: string; client_secret: string }> {
  return request('/api/billing/card-setup', { method: 'POST' });
}

// Makes the saved card the default so the retried plan change can charge it.
export async function completeCardSetup(sessionId: string): Promise<{ ok: boolean }> {
  return request(`/api/billing/card-setup/${encodeURIComponent(sessionId)}/complete`, {
    method: 'POST',
  });
}

// Web only: Stripe Customer Portal for the billing owner.
export async function createPortalSession(): Promise<{ url: string }> {
  return request('/api/billing/portal-session', { method: 'POST' });
}

// Web/Stripe only (native uses googleProductChangeInfo); country is hardcoded as MY is the only Stripe market.
export async function changePlan(target: {
  seatTier: string;
  pricingTier: string;
  billingPeriod: 'monthly' | 'yearly';
}): Promise<{ ok: boolean; deferred_until?: number | null }> {
  return request('/api/billing/change-plan', {
    method: 'POST',
    body: JSON.stringify({
      seat_tier: target.seatTier,
      pricing_tier: target.pricingTier,
      billing_period: target.billingPeriod,
      country: 'MY',
    }),
  });
}

// Web/Stripe only: it can't issue the store replacement needed to revert on Play.
export async function cancelPendingChange(): Promise<{ ok: boolean }> {
  return request('/api/billing/cancel-pending-change', { method: 'POST' });
}

// Play only; call fire-and-forget and never surface a failure, as the store change is already real.
export async function recordPendingChange(target: {
  seatTier: string;
  pricingTier: string;
}): Promise<{ ok: boolean }> {
  return request('/api/billing/pending-change', {
    method: 'POST',
    body: JSON.stringify({ seat_tier: target.seatTier, pricing_tier: target.pricingTier }),
  });
}

export const SEAT_TIER_NAMES: Record<string, string> = {
  individual: 'Family Solo',
  family_duo: 'Family Duo',
  family_extended: 'Family Extended',
};

// Webhook-driven source of truth; never substitute the client SDK's view.
export async function getAccess(): Promise<AccessResponse> {
  return request('/api/access');
}

export async function getMyCode(): Promise<ParentPairingResponse> {
  return request<ParentPairingResponse>('/api/pairing/my-code');
}

export async function regenerateMyCode(): Promise<ParentPairingResponse> {
  return request<ParentPairingResponse>('/api/pairing/my-code/regenerate', {
    method: 'POST',
  });
}

/** TUP-1835: redeeming consents AND links in one step for every age, so the
 *  caller must have shown the consent step first — the body always carries
 *  the acknowledgement (the server answers 400 consent_required without it).
 *  childName is the name the parent typed for a child who has none yet
 *  (TUP-1846); the server answers 400 child_name_required without it then. */
export async function redeemChildCode(
  code: string,
  childName?: string
): Promise<RedeemCodeResponse> {
  return request<RedeemCodeResponse>('/api/pairing/redeem', {
    method: 'POST',
    body: JSON.stringify({
      code,
      ...consentPayload(),
      ...(childName ? { child_name: childName } : {}),
    }),
  });
}

/** Pre-redeem preview: child's first name, whether they are already linked
 *  to this parent, and whether a seat is free. Authenticated. */
export async function getPairingCodeInfo(code: string): Promise<PairingCodeInfoResponse> {
  return request<PairingCodeInfoResponse>(
    `/api/pairing/code-info?code=${encodeURIComponent(code)}`
  );
}

/** PPLT-042: child-agnostic capacity pre-check for the "+ Add child" entry
 *  point — lets ChildrenPage gate itself before opening LinkChildSheet at
 *  all, instead of only discovering "no seats left" after the parent has
 *  typed a code in. Throws ApiError (code seat_limit_reached /
 *  seat_limit_reached_max_tier / trial_seat_locked / feature_blocked) when
 *  at capacity — same codes GateNoticeModal already maps to copy. */
export async function getSeatCapacity(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/pairing/seat-check');
}

export async function getParentLinks(): Promise<ParentLinksResponse> {
  return request<ParentLinksResponse>('/api/pairing/links');
}

/** Whether the parent's plan has room for this child's request, checked before the consent step. */
export async function checkLinkSeat(
  linkId: string
): Promise<{ seat_ok: boolean; seat_error: string | null }> {
  return request(`/api/pairing/links/${linkId}/seat-check`);
}

/** Approving is a consent act too (TUP-1835): the caller shows the consent
 *  step first and the body carries the acknowledgement. */
export async function approveLink(linkId: string, childName?: string): Promise<void> {
  await request(`/api/pairing/links/${linkId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ ...consentPayload(), ...(childName ? { child_name: childName } : {}) }),
  });
}

export async function rejectLink(linkId: string): Promise<void> {
  await request(`/api/pairing/links/${linkId}/reject`, { method: 'POST' });
}

export async function revokeLink(linkId: string): Promise<void> {
  await request(`/api/pairing/links/${linkId}/revoke`, { method: 'POST' });
}

export interface UpdateAccountResponse {
  parent_name: string;
  phone: string | null;
  preferred_language: string;
}

/**
 * Every field is optional and omitted keys are left out of the body entirely,
 * which the server COALESCEs — so each caller sends only the fact it owns and
 * cannot clobber the others. That matters in both directions: a name edit
 * must not reset the language, and the language sheet must not write back a
 * name it read from a possibly-stale auth store. The phone isn't here: a new
 * number is SMS-verified with Firebase and saved via syncAccountPhone.
 */
export async function updateAccount(fields: {
  parentName?: string;
  preferredLanguage?: AppLanguage;
}): Promise<UpdateAccountResponse> {
  return request<UpdateAccountResponse>('/account', {
    method: 'PATCH',
    body: JSON.stringify({
      ...(fields.parentName !== undefined && { parent_name: fields.parentName }),
      ...(fields.preferredLanguage !== undefined && {
        preferred_language: fields.preferredLanguage,
      }),
    }),
  });
}

/** Whether no other account already logs in with this email. */
export async function isEmailAvailable(email: string): Promise<boolean> {
  const res = await request<{ available: boolean }>('/account/email/available', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return res.available;
}

/** Whether no other account already uses this phone (E.164). */
export async function isPhoneAvailable(phone: string): Promise<boolean> {
  const res = await request<{ available: boolean }>('/account/phone/available', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
  return res.available;
}

/** Copies the parent's Firebase-verified phone onto their account. */
export async function syncAccountPhone(): Promise<{ phone: string }> {
  return request<{ phone: string }>('/account/phone/sync', { method: 'POST' });
}

export async function deleteAccount(): Promise<void> {
  await request('/account', { method: 'DELETE' });
}

export interface ChapterSummary {
  chapter_id: string;
  chapter_num: number;
  title: string;
  title_ms?: string;
  status: string;
}

export async function getChapters(syllabus: string, level: string): Promise<ChapterSummary[]> {
  return request<ChapterSummary[]>(
    `/api/chapters?syllabus=${syllabus}&level=${encodeURIComponent(level)}`
  );
}

export type { AssignmentSummary } from '@tupai/types';

export async function assignPractice(
  linkId: string,
  chapter: { chapter_id: string; chapter_title: string; chapter_title_ms?: string }
): Promise<{ assignment_id: string; assigned_at: string; status: string }> {
  return request(`/api/children/${linkId}/assignments`, {
    method: 'POST',
    body: JSON.stringify(chapter),
  });
}

export async function getCreatedAssignments(): Promise<AssignmentsResponse> {
  return request('/api/children/assignments');
}

/** One child's homework: the parent's created assignments, narrowed to that link. */
export async function getChildAssignments(linkId: string): Promise<AssignmentSummary[]> {
  if (isSampleLink(linkId)) return sampleAssignments();
  const res = await getCreatedAssignments();
  return res.assignments.filter((a) => a.link_id === linkId);
}

export type { AssignmentResultResponse } from '@tupai/types';

export async function getAssignmentResult(assignmentId: string): Promise<AssignmentResultResponse> {
  if (isSampleId(assignmentId)) return sampleAssignmentResult(assignmentId);
  return request(`/api/children/assignments/${assignmentId}/result`);
}

export interface AssignmentWorksheetStep {
  text: string;
  inputType: 'mcq' | 'text';
  options: string[];
}

export interface AssignmentWorksheetQuestion {
  number: number;
  stem: string;
  hasImage: boolean;
  imageUrl?: string;
  steps: AssignmentWorksheetStep[];
}

export interface AssignmentWorksheetResponse {
  chapter_title: string;
  student_name: string;
  questions: AssignmentWorksheetQuestion[];
}

export async function getAssignmentWorksheet(
  assignmentId: string,
  lang: WorksheetLanguage = 'en'
): Promise<AssignmentWorksheetResponse> {
  return request(`/api/children/assignments/${assignmentId}/worksheet?lang=${lang}`);
}

export interface ExamSummary {
  id: number;
  title: string;
  subject: string;
  variant?: string;
  series?: string;
  questions: number;
  time: string;
  tag: string;
  form?: string;
  year: number;
}

export async function getExams(syllabus: string): Promise<ExamSummary[]> {
  return request<ExamSummary[]>(`/api/exams?syllabus=${syllabus}`);
}

export interface ExamWorksheetQuestion {
  parts?: ExamWorksheetPart[];
  questionStem?: string;
  questionImageUrls?: string[];
  number: number;
  stem: string;
  options?: string[];
  hasImage: boolean;
  imageUrl?: string;
  imageUrls?: string[];
}

export type WorksheetLanguage = 'en' | 'ms';

/**
 * The parent's UI language, stored on `parents.preferred_language`. An alias of
 * the language store's AppLocale rather than a second union, so the two can
 * never drift. Still named separately from WorksheetLanguage because they are
 * different facts: that one is the language a downloaded PDF is generated in,
 * chosen per download; this is the language the app renders in.
 */
export type AppLanguage = AppLocale;

export interface ChildSummaryAppUsage {
  activeDays: number;
  totalDays: number;
  totalMins: number;
  dailyMins: number[];
}

export interface ChildSummaryRecommendation {
  chapterNum: number;
  name: string;
  chapterId: string;
  syllabus: string;
  grade: string;
}

export interface ChildSummaryResponse {
  // Optional for older servers; report_viewed skips firing when absent.
  id?: string;
  title: string | null;
  appUsage: ChildSummaryAppUsage;
  overview: string;
  strengths: string[];
  improvements: string[];
  recommendations: ChildSummaryRecommendation[];
  accuracy: number;
  generated_at: string;
  period_start: string;
  period_end: string;
  period_days: number;
  can_regenerate?: boolean;
  cooldown_until: string | null;
  from_cache: boolean;
  read?: boolean;
}

export async function getChildSummary(linkId: string): Promise<ChildSummaryResponse> {
  return request<ChildSummaryResponse>(`/api/children/${linkId}/summary`);
}

export async function regenerateChildSummary(linkId: string): Promise<ChildSummaryResponse> {
  return request<ChildSummaryResponse>(`/api/children/${linkId}/summary/regenerate`, {
    method: 'POST',
  });
}

export async function getChildSummaryHistory(linkId: string): Promise<ChildSummaryHistoryResponse> {
  if (isSampleLink(linkId)) return sampleReportHistory();
  return request<ChildSummaryHistoryResponse>(`/api/children/${linkId}/summary/history`);
}

export async function getChildSummaryHistoryReport(
  linkId: string,
  reportId: string
): Promise<ChildSummaryResponse> {
  if (isSampleLink(linkId)) return sampleReport(reportId);
  return request<ChildSummaryResponse>(`/api/children/${linkId}/summary/history/${reportId}`);
}

export async function getChildActivity(linkId: string): Promise<ChildActivityResponse> {
  if (isSampleLink(linkId)) return sampleActivity();
  return request<ChildActivityResponse>(`/api/children/${linkId}/activity`);
}

export async function getNotifications(): Promise<ParentNotificationsResponse> {
  return request<ParentNotificationsResponse>('/notifications');
}

/** Marks one notification read by id, for a tapped push. */
export async function markNotificationReadById(notificationId: string): Promise<void> {
  await request(`/notifications/${notificationId}/read`, { method: 'POST' });
}

export async function markNotificationRead(
  kind: ParentNotificationKind,
  sourceId: string
): Promise<void> {
  if (isSampleId(sourceId)) return;
  await request('/notifications/read', {
    method: 'POST',
    body: JSON.stringify({ kind, source_id: sourceId }),
  });
}

function worksheetPartUrls(part: ExamWorksheetPart): ExamWorksheetPart {
  const resolve = (url: string) => (url.startsWith('/api/') ? `${BASE}${url}` : url);
  return {
    ...part,
    imageUrls: part.imageUrls.map(resolve),
    options: part.options.map(resolve),
    subParts: part.subParts?.map(worksheetPartUrls),
  };
}

export async function getExamQuestions(
  paperId: number,
  lang: WorksheetLanguage = 'en'
): Promise<{ paper_id: number; questions: ExamWorksheetQuestion[] }> {
  const { paper_id, questions } = await request<{
    paper_id: number;
    questions: ExamWorksheetQuestion[];
  }>(`/api/exams/${paperId}/questions?lang=${lang}`);
  // Image paths are parent-api routes; resolve against BASE so <img src> works from this app.
  return {
    paper_id,
    questions: questions.map((q) => ({
      ...q,
      parts: q.parts?.map(worksheetPartUrls),
      questionImageUrls: q.questionImageUrls?.map((url) =>
        url.startsWith('/api/') ? `${BASE}${url}` : url
      ),
      imageUrl: q.imageUrl ? `${BASE}${q.imageUrl}` : undefined,
      imageUrls: q.imageUrls?.map((url) => `${BASE}${url}`),
      options: q.options?.map((opt) => (opt.startsWith('/api/') ? `${BASE}${opt}` : opt)),
    })),
  };
}
